import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import useSidePanel from "../../../shared/useSidePanel";
import {
  COLUMN_BY_KEY,
  CONFIGURABLE_COLUMNS,
  DEFAULT_COLUMN_ORDER,
} from "../../constants/expandedColumns";
import "./ColumnSettings.css";

const SIDE_GROUPS = [
  { side: "dt", label: "Дебет" },
  { side: "kt", label: "Кредит" },
];

/** Вставка key на позицию index (позиция считается в исходном списке) */
const moveKey = (order, key, index) => {
  const from = order.indexOf(key);
  const next = order.filter((item) => item !== key);
  const target = from !== -1 && from < index ? index - 1 : index;
  next.splice(target, 0, key);
  return next;
};

function ColumnSettings({ columnOrder, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(columnOrder);
  const [query, setQuery] = useState("");
  const [draggedKey, setDraggedKey] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const handleClose = () => setIsOpen(false);
  const headerOffset = useSidePanel(isOpen, handleClose);

  const draftSet = useMemo(() => new Set(draft), [draft]);
  const totalCount = CONFIGURABLE_COLUMNS.length;
  const isCustomized =
    columnOrder.length !== DEFAULT_COLUMN_ORDER.length ||
    columnOrder.some((key, index) => key !== DEFAULT_COLUMN_ORDER[index]);

  const availableGroups = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return SIDE_GROUPS.map((group) => ({
      ...group,
      columns: CONFIGURABLE_COLUMNS.filter(
        (column) =>
          column.side === group.side &&
          !draftSet.has(column.key) &&
          (!normalized || column.label.toLowerCase().includes(normalized))
      ),
    })).filter((group) => group.columns.length > 0);
  }, [draftSet, query]);

  const hasAvailable = draft.length < totalCount;

  const handleOpen = () => {
    setDraft(columnOrder);
    setQuery("");
    setIsOpen(true);
  };

  const handleApply = () => {
    onChange(draft);
    setIsOpen(false);
  };

  const resetDrag = () => {
    setDraggedKey(null);
    setDropIndex(null);
  };

  const handleDragStart = (event, key) => {
    event.dataTransfer.effectAllowed = "move";
    // Firefox не начинает перетаскивание без данных
    event.dataTransfer.setData("text/plain", key);
    setDraggedKey(key);
  };

  const handleItemDragOver = (event, index) => {
    if (!draggedKey) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;
    setDropIndex(isAfter ? index + 1 : index);
  };

  const handleListDragOver = (event) => {
    if (!draggedKey) return;
    event.preventDefault();
    if (event.target === event.currentTarget) setDropIndex(draft.length);
  };

  const handleListDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setDropIndex(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (draggedKey && dropIndex !== null) {
      setDraft((order) => moveKey(order, draggedKey, dropIndex));
    }
    resetDrag();
  };

  const trigger = (
    <button type="button" className="ojc-trigger" onClick={handleOpen}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect
          x="2"
          y="2.5"
          width="12"
          height="11"
          rx="1.5"
          stroke="currentColor"
          strokeWidth="1.2"
        />
        <path d="M6 2.5V13.5M10 2.5V13.5" stroke="currentColor" strokeWidth="1.2" />
      </svg>
      Настроить колонки
      {isCustomized && (
        <span className="ojc-trigger__badge">
          {columnOrder.length}/{totalCount}
        </span>
      )}
    </button>
  );

  if (!isOpen) return trigger;

  const drawer = (
    <div
      className="ojc-overlay"
      style={{ top: headerOffset }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <aside
        className="ojc-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ojc-drawer-title"
      >
        <header className="ojc-drawer__header">
          <h2 id="ojc-drawer-title" className="ojc-drawer__title">
            Настроить колонки
          </h2>
          <div className="ojc-drawer__header-actions">
            <button
              type="button"
              className="ojc-drawer__close"
              aria-label="Закрыть"
              onClick={handleClose}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1.5 1.5L12.5 12.5M12.5 1.5L1.5 12.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="ojc-drawer__reset"
              onClick={() => setDraft(DEFAULT_COLUMN_ORDER)}
            >
              Сбросить Все
            </button>
          </div>
        </header>

        <div className="ojc-drawer__body">
          <section className="ojc-panel">
            <div className="ojc-panel__head">
              <h3 className="ojc-panel__title">Доступные поля</h3>
            </div>
            <div className="ojc-search">
              <svg
                className="ojc-search__icon"
                width="14"
                height="14"
                viewBox="0 0 18 18"
                fill="none"
              >
                <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.6" />
                <path
                  d="M12.5 12.5L15.5 15.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="text"
                className="ojc-search__input"
                placeholder="Поиск поля..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>

            <div className="ojc-panel__scroll">
              {!hasAvailable && (
                <div className="ojc-empty">Все поля уже выбраны</div>
              )}
              {hasAvailable && availableGroups.length === 0 && (
                <div className="ojc-empty">Ничего не найдено</div>
              )}
              {availableGroups.map((group) => (
                <div key={group.side} className="ojc-group">
                  <div className="ojc-group__label">{group.label}</div>
                  {group.columns.map((column) => (
                    <button
                      key={column.key}
                      type="button"
                      className={`ojc-available${
                        draggedKey === column.key ? " is-dragging" : ""
                      }`}
                      draggable
                      onDragStart={(event) => handleDragStart(event, column.key)}
                      onDragEnd={resetDrag}
                      onClick={() => setDraft((order) => [...order, column.key])}
                    >
                      <span className="ojc-available__plus">+</span>
                      {column.label}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section className="ojc-panel ojc-panel--selected">
            <div className="ojc-panel__head">
              <h3 className="ojc-panel__title">Выбранные поля</h3>
              <p className="ojc-panel__hint">
                Перетащите поля для изменения порядка
              </p>
            </div>

            <div
              className="ojc-panel__scroll ojc-selected"
              onDragOver={handleListDragOver}
              onDragLeave={handleListDragLeave}
              onDrop={handleDrop}
            >
              {draft.length === 0 && (
                <div className="ojc-empty">Добавьте поля из списка слева</div>
              )}
              {draft.map((key, index) => {
                const classes = ["ojc-selected__item"];
                if (draggedKey === key) classes.push("is-dragging");
                if (dropIndex === index) classes.push("is-drop-before");
                if (dropIndex === draft.length && index === draft.length - 1) {
                  classes.push("is-drop-after");
                }

                return (
                  <div
                    key={key}
                    className={classes.join(" ")}
                    draggable
                    onDragStart={(event) => handleDragStart(event, key)}
                    onDragOver={(event) => handleItemDragOver(event, index)}
                    onDragEnd={resetDrag}
                  >
                    <span className="ojc-selected__handle" aria-hidden>
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor">
                        <circle cx="2" cy="2" r="1.1" />
                        <circle cx="6" cy="2" r="1.1" />
                        <circle cx="2" cy="6" r="1.1" />
                        <circle cx="6" cy="6" r="1.1" />
                        <circle cx="2" cy="10" r="1.1" />
                        <circle cx="6" cy="10" r="1.1" />
                      </svg>
                    </span>
                    <span className="ojc-selected__label">
                      {COLUMN_BY_KEY[key].label}
                    </span>
                    <button
                      type="button"
                      className="ojc-selected__remove"
                      aria-label={`Убрать «${COLUMN_BY_KEY[key].label}»`}
                      onClick={() =>
                        setDraft((order) => order.filter((item) => item !== key))
                      }
                    >
                      <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                        <path
                          d="M1.5 1.5L8.5 8.5M8.5 1.5L1.5 8.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <footer className="ojc-drawer__footer">
          <button type="button" className="ojc-btn ojc-btn--secondary" onClick={handleClose}>
            Отмена
          </button>
          <button type="button" className="ojc-btn ojc-btn--primary" onClick={handleApply}>
            Применить
          </button>
        </footer>
      </aside>
    </div>
  );

  return (
    <>
      {trigger}
      {createPortal(drawer, document.body)}
    </>
  );
}

export default ColumnSettings;
